import { History } from 'history';

import { AuthFetch, HttpError, LoggableError, ValidationError } from '../../../common/index';

import { JwtStorage } from '../../auth';
import { ErrorMessageComponent } from './ErrorMessage';

export class ErrorLogger {
  private history: History;
  private component: ErrorMessageComponent;

  errors: LoggableError[] = [];
  validationMessages: string[] = [];

  constructor(public shortEnv: string, private jwtStorage: JwtStorage, private authFetch?: AuthFetch) { }

  registerComponent(history: History, component: ErrorMessageComponent) {
    this.history = history;
    this.component = component;
  }

  clearErrors() {
    this.errors = [];
    this.validationMessages = [];

    if (this.component) {
      this.component.setState({ errors: [], validationMessages: [] });
    }
  }

  /**
   * Adds an error to the error messages.
   * @param {Error} error the error to add
   */
  async log(error: Error): Promise<void> {
    if (!error || !this.history) {
      return;
    }

    let httpError = error as HttpError;
    const validationError = httpError as ValidationError;

    if (httpError.statusCode === 400 && validationError.messages && validationError.messages.length > 0) {
      if (validationError.messages.length === this.validationMessages.length &&
        validationError.messages.every(existing => this.validationMessages.includes(existing))
      ) {
        return;
      }

      this.pushToComponent( null, validationError.messages );
      return;
    }

    if (httpError.statusCode === 401) {
      this.jwtStorage.clear();

      // If the token is corrupt throw access denied error
      if (( error && error.message === 'CORRUPT_TOKEN' )) {
        this.component.onLoading(false);
        this.history.push('/not-authorized');
        return;
      }

      try {
        this.jwtStorage.login();
        return;
      } catch (loginError) {
        httpError = loginError;
      }
    }

    if (httpError.statusCode === 403) {
      this.component.onLoading(false);
      this.history.push('/not-authorized');
      return;
    }

    if (httpError.statusCode === 404) {
      this.component.onLoading(false);
      this.history.push('/not-found');
      return;
    }

    if (this.errors.some(existing => existing.message === error.message)) {
      return;
    }

    const loggableError = LoggableError.create(error);

    this.pushToComponent(loggableError);

    if (!httpError.statusCode) {
      await this.logToServer(loggableError);
    }
  }

  private async logToServer(error: LoggableError): Promise<void> {
    if (!error || !error.message || !this.authFetch) {
      return;
    }

    const payload = {
      code: 'CLIENT_ERROR',
      // Trim the client error eliminating new lines in the case where the HTML is dumped out
      message: error.message.indexOf('\n') > 100 ? error.message.split('\n')[0] : error.message,
      url: window.location.href,
      stack: ( error.stack || '' )
        .split('\n')
        .slice(0, 20)
        .map(lines => lines.toString())
        .join('\n')
    };

    try {
      await this.authFetch('/api/system/error', { method: 'POST', body: payload });
    } catch (err) { /* Hide logging error */ }
  }

  private pushToComponent(error: LoggableError | null, messages?: string[]) {
    if (messages) {
      this.validationMessages = [...this.validationMessages, ...messages];
    }

    if (error) {
      this.errors = [...this.errors, error];
    }

    this.component.setState({ errors: this.errors, validationMessages: this.validationMessages });

    window.scrollTo(0, 0);
  }
}



// WEBPACK FOOTER //
// ./src/gs-core/client/react/error/ErrorLogger.ts